<template>
  <div class="booking-stepper mb-8 sm:mb-16">
    <div class="flex items-start justify-center">
      <!-- Step 1: Select -->
      <div class="flex flex-col items-center">
        <button
          @click="!locked && $emit('go-to-step', 1)"
          :disabled="currentStep === 1 || locked"
          class="flex flex-col items-center group sm:flex-row"
          :class="{
            'cursor-default': currentStep === 1 || locked,
            'cursor-pointer': currentStep > 1 && !locked
          }"
        >
          <div
            :class="[
              'w-8 h-8 flex items-center justify-center text-sm font-medium transition-all border',
              currentStep >= 1
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-400 border-gray-300'
            ]"
          >
            <svg v-if="currentStep > 1" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
            <span v-else>1</span>
          </div>
          <span
            :class="[
              'mt-1 text-[10px] sm:text-xs font-medium uppercase tracking-wider sm:ml-2 sm:mt-0',
              currentStep >= 1 ? 'text-gray-900' : 'text-gray-400'
            ]"
          >
            {{ $t('booking.step.select') }}
          </span>
        </button>
      </div>

      <!-- Connector -->
      <div
        :class="[
          'w-8 sm:w-14 h-px mx-2 sm:mx-3 transition-all mt-4',
          currentStep >= 2 ? 'bg-gray-900' : 'bg-gray-200'
        ]"
      />

      <!-- Step 2: Appoint -->
      <div class="flex flex-col items-center">
        <button
          @click="!locked && $emit('go-to-step', 2)"
          :disabled="currentStep <= 2 || locked"
          class="flex flex-col items-center group sm:flex-row"
          :class="{
            'cursor-default': currentStep === 2 || locked,
            'cursor-pointer': currentStep > 2 && !locked,
            'cursor-not-allowed opacity-50': currentStep < 2
          }"
        >
          <div
            :class="[
              'w-8 h-8 flex items-center justify-center text-sm font-medium transition-all border',
              currentStep >= 2
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-400 border-gray-300'
            ]"
          >
            <svg v-if="currentStep > 2" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
            <span v-else>2</span>
          </div>
          <span
            :class="[
              'mt-1 text-[10px] sm:text-xs font-medium uppercase tracking-wider sm:ml-2 sm:mt-0',
              currentStep >= 2 ? 'text-gray-900' : 'text-gray-400'
            ]"
          >
            {{ $t('booking.step.appoint') }}
          </span>
        </button>
      </div>

      <!-- Connector -->
      <div
        :class="[
          'w-8 sm:w-14 h-px mx-2 sm:mx-3 transition-all mt-4',
          currentStep >= 3 ? 'bg-gray-900' : 'bg-gray-200'
        ]"
      />

      <!-- Step 3: Checkout -->
      <div class="flex flex-col items-center">
        <button
          @click="!locked && $emit('go-to-step', 3)"
          :disabled="currentStep <= 3 || locked"
          class="flex flex-col items-center group sm:flex-row"
          :class="{
            'cursor-default': currentStep === 3 || locked,
            'cursor-pointer': currentStep > 3 && !locked,
            'cursor-not-allowed opacity-50': currentStep < 3
          }"
        >
          <div
            :class="[
              'w-8 h-8 flex items-center justify-center text-sm font-medium transition-all border',
              currentStep >= 3
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-400 border-gray-300'
            ]"
          >
            <svg v-if="currentStep > 3" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
            <span v-else>3</span>
          </div>
          <span
            :class="[
              'mt-1 text-[10px] sm:text-xs font-medium uppercase tracking-wider sm:ml-2 sm:mt-0',
              currentStep >= 3 ? 'text-gray-900' : 'text-gray-400'
            ]"
          >
            {{ $t('booking.step.checkout') }}
          </span>
        </button>
      </div>

      <!-- Connector -->
      <div
        :class="[
          'w-8 sm:w-14 h-px mx-2 sm:mx-3 transition-all mt-4',
          currentStep >= 4 ? 'bg-gray-900' : 'bg-gray-200'
        ]"
      />

      <!-- Step 4: Payment -->
      <div class="flex flex-col items-center">
        <div class="flex flex-col items-center sm:flex-row">
          <div
            :class="[
              'w-8 h-8 flex items-center justify-center text-sm font-medium transition-all border',
              currentStep >= 4
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-400 border-gray-300'
            ]"
          >
            <span>4</span>
          </div>
          <span
            :class="[
              'mt-1 text-[10px] sm:text-xs font-medium uppercase tracking-wider sm:ml-2 sm:mt-0',
              currentStep >= 4 ? 'text-gray-900' : 'text-gray-400'
            ]"
          >
            {{ $t('booking.step.payment') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

interface Props {
  currentStep: number
  locked?: boolean
}

defineProps<Props>()

defineEmits<{
  (e: 'go-to-step', step: number): void
}>()

const stepLabels = [
  t('booking.step.select'),
  t('booking.step.appoint'),
  t('booking.step.checkout'),
  t('booking.step.payment')
]
</script>
